import { forwardRef, useCallback, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'

import BaseTripItemSummaryCard from './BaseTripItemSummaryCard'
import { startPlaceOfTripItem } from '../../utils'

import TextButton from 'LuxKit/Button/TextButton'
import GeoContext from 'contexts/geoContext'
import useNavigateTo from 'hooks/useNavigateTo'
import { useRegionTimeFormat } from 'hooks/useRegionTimeFormat'
import BookmarkLocation from 'tripPlanner/components/Bookmark/BookmarkCardNew/BookmarkLocation'
import PlannedExperienceCtaContent from 'tripPlanner/components/ItemCard/Experience/PlannedExperienceCtaContent'
import ExperienceTilePrices from 'components/Experiences/ExperienceTiles/ExperienceTilePrices'
import { CurrencyProvider } from 'contexts/currencyContext'
import BodyText from 'LuxKit/Typography/BodyText'
import useTripPlannerCTALabels from 'tripPlanner/hooks/useTripPlannerCTALabels'
import useCanEditTrip from 'tripPlanner/hooks/useCanEditTrip'
import { useExperience } from 'tripPlanner/hooks/api/offer'
import { usePlaceRating } from 'tripPlanner/hooks/Google/usePlaceRating'
import type { ExperienceItem } from 'tripPlanner/types/tripItem'
import {
  buildMyEscapesOrderLink,
  formatDateTimeOrDateRange,
  isCustomItem,
  isLeBooking,
  isLeBookmark,
} from 'tripPlanner/utils'
import { buildProductLink } from 'tripPlanner/utils/bookmark/restoreCart'
import { isExperienceAvailable } from 'tripPlanner/utils/experience'

interface Props {
  className?: string
  item: ExperienceItem
  showCloseButton?: boolean
  onClose?: () => void
}

const ExperienceItemSummaryCard = forwardRef<HTMLDivElement, Props>(
  ({ className, item, showCloseButton, onClose }, ref) => {
    const intl = useIntl()
    const { bookingDetailsCopy } = useTripPlannerCTALabels()
    const { currentCurrency, currentRegionCode } = useContext(GeoContext)
    const canEdit = useCanEditTrip()
    const place = startPlaceOfTripItem(item)
    const { data: rating } = usePlaceRating(
      place?.id ?? '',
      place?.id !== undefined && isCustomItem(item),
    )
    const experienceId = item.code ?? ''

    const { data: experience } = useExperience({
      experienceId,
      currentCurrency,
      currentRegionCode,
    })
    const offerImageId = experience?.images?.[0]?.id
    const imageURL = experience?.images?.[0]?.url

    const offerLink = useMemo(() => {
      return isLeBookmark(item) ?
          buildProductLink(item, { experience }) :
        undefined
    }, [experience, item])

    const myEscapesLink =
      canEdit ? buildMyEscapesOrderLink(item) : undefined

    const navigateTo = useNavigateTo()

    const isOfferAvailable = experience ?
        isExperienceAvailable(experience) :
      false

    const viewBooking = useCallback(() => {
      if (isLeBooking(item) && myEscapesLink) {
        navigateTo(myEscapesLink)
      }
    }, [item, myEscapesLink, navigateTo])

    const priceAreaContent = useMemo(() => {
      if (!isLeBookmark(item)) return null
      if (!isOfferAvailable) {
        return <BodyText variant="large" weight="bold">{intl.formatMessage({
          id: 'J7dBW-9sOi4TdiC_D1rc',
          defaultMessage: 'Sold Out',
          description: 'Label shown when an offer is no longer available in the trip planner',
        })}
        </BodyText>
      }
      const { price, discountPercent, currencyCode, memberPrice, value, memberDiscountPercent } = item.savedItemData
      if (!price) return null
      return (
        <CurrencyProvider currency={currencyCode}>
          <ExperienceTilePrices
            totalPrice={price}
            totalMemberPrice={memberPrice}
            totalGrossPrice={value}
            discountPercentage={discountPercent}
            discountMemberPercentage={memberDiscountPercent}
            isFromPrice
            ticketUnitLabel="person"
          />
        </CurrencyProvider>
      )
    }, [intl, isOfferAvailable, item])

    const ctaAreaContent = useMemo(() => {
      if (isLeBookmark(item) && offerLink) {
        return (
          <PlannedExperienceCtaContent
            item={item}
            isOfferAvailable={isOfferAvailable}
            variant="mapSummary"
            offerLink={offerLink}
          />
        )
      } else if (isLeBooking(item)) {
        return (
          <>
            <div />
            <TextButton
              kind="secondary"
              size="small"
              variant="default"
              nonInteractable
              onClick={viewBooking}
            >
              {bookingDetailsCopy}
            </TextButton>
          </>
        )
      }
    }, [isOfferAvailable, item, viewBooking, offerLink, bookingDetailsCopy])

    const locationDescriptionContent = useMemo(() => {
      return (
        <BookmarkLocation
          variant="with_experience"
          startLocation={experience?.location.name}
          typeOfExperience={experience?.primaryCategory?.name}
          showIcon={false}
        />
      )
    }, [experience])

    const timeFormat = useRegionTimeFormat()
    const timeDescription = formatDateTimeOrDateRange(item, timeFormat)

    if (!place) {
      return null
    }

    return (
      <BaseTripItemSummaryCard
        ref={ref}
        className={className}
        item={item}
        showCloseButton={showCloseButton}
        onClose={onClose}
        offerImageId={offerImageId}
        imageURL={imageURL}
        offerLink={offerLink}
        rating={rating}
        place={place}
        priceAreaContent={priceAreaContent}
        ctaAreaContent={ctaAreaContent}
        locationDescriptionContent={locationDescriptionContent}
        descriptors={[timeDescription]}
      />
    )
  },
)

export default ExperienceItemSummaryCard
